<template>
  <div
    ref="topGap"
    class="c-pt-3 hidden c-lg:block"
  />
  <nav
    id="js-navbar"
    class="navbar"
  >
    <div class="overflow-hidden">
      <div
        class="hidden c-xl:block absolute w-100 h-100"
        :class="backgroundColor"
        :style="{ opacity: isFixed ? 1 : 0 }"
      />
      <div class="container">
        <div class="form-row c-flex-nowrap c-items-center c-py-1 c-lg:c-py-0 c-lg:c-mx-0 c-xxl:c-px-5">
          <Logo v-once />
          <SearchForm />
          <!-- right side of navbar (menu, login, favourite, basket, language,..) -->
          <div class="navbar__rightside col-auto c-ml-auto c-lg:col c-lg:c-ml-0 c-xl:c-pl-4 flex c-self-stretch">
            <div class="flex c-flex-col c-justify-center c-flex-grow-1 relative">
              <div class="form-row c-items-center">
                <TheNavbarMenu />
                <Toolbar v-once />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="freeDeliveryMsgShow && isFreeTransportNotice"
      class="cart-msg-js relative"
    >
      <div
        id="cart-message"
        class="c-py-1 c-px-3 text-white free-delivery free-delivery__top"
      >
        <p
          v-if="hasFreeTransport || (isSignedIn && getCurrentCustomer.inPlusClub)"
          class="c-m-0 font-bold"
        >
          {{ t('you_have_free_shipping') }}
        </p>
        <p
          v-else
          class="c-m-0 font-bold"
        >
          {{ t('missing_free_shipping') }} {{ numberToCurrency(freeShippingPrice, t('currency')) }}
        </p>
        <button
          type="button"
          class="btn c-p-0 c-m-0 border-none c-ml-3"
          aria-label="Close free delivery tooltip"
          data-cy="close-free-delivery-msg"
          @click="freeShippingMsgHandler"
        >
          <i class="mi-times" />
        </button>
      </div>
    </div>
  </nav>
  <BaseModal
    :title="t('watchdog_title')"
    modal-id="modal-watchdog"
  >
    <ModalWatchdog />
  </BaseModal>
</template>

<script setup>
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseModal from '@/components/general/BaseModal.vue'
import Logo from '@/components/menus/navbar/TheNavbarLogo.vue'
import TheNavbarMenu from '@/components/menus/navbar/TheNavbarMenu.vue'
import SearchForm from '@/components/menus/navbar/TheNavbarSearchForm.vue'
import Toolbar from '@/components/menus/navbar/TheNavbarToolbar.vue'
import ModalWatchdog from '@/components/modals/ModalWatchdog.vue'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'
import { useNavbarStore } from '@/stores/NavbarStore'

const { t } = useI18n()

const { backgroundColor } = storeToRefs(useNavbarStore())
const cartStore = useCartStore()
const appStore = useAppStore()
const { isFreeTransportNotice, isSignedIn, getCurrentCustomer } = storeToRefs(appStore)
const { hasFreeTransport } = storeToRefs(cartStore)
const topGap = ref()
const isFixed = ref(false)
const freeDeliveryMsgShow = ref(true)

const freeShippingPrice = computed(() => {
  if (cartStore.getTotalItemsInCart > 0) {
    return cartStore.getCart?.priceUntilFreeTransport
  }
  return t('free_shipping')
})

useIntersectionObserver(topGap, ([{ isIntersecting }]) => {
  isFixed.value = !isIntersecting
})

const freeShippingMsgHandler = () => {
  freeDeliveryMsgShow.value = false
}

watch(
  () => hasFreeTransport.value,
  () => {
    if (hasFreeTransport.value === true) freeDeliveryMsgShow.value = true
  }
)
</script>

<i18n>
  {
    "cs": {
    "currency": "Kč",
    "free_shipping": "1499",
    "free_shipping_from": "Doprava zdarma od 1499 Kč",
    "missing_free_shipping": "Do dopravy zdarma chybí",
    "watchdog_title": "Chcete si počkat?",
    "you_have_free_shipping": "Dopravu máte zdarma! 🎉"
},
    "sk": {
    "currency": "€",
    "free_shipping": "59",
    "free_shipping_from": "Doprava zdarma od 59 €",
    "missing_free_shipping": "Do dopravy zadarmo chýba",
    "watchdog_title": "Chcete si počkať?",
    "you_have_free_shipping": "Dopravu máte zdarma! 🎉"
},
    "pl": {
    "currency": "zł",
    "free_shipping": "199",
    "free_shipping_from": "Darmowa dostawa od 199 zł",
    "missing_free_shipping": "Do darmowej dostawy brakuje",
    "watchdog_title": "Chcesz poczekać?",
    "you_have_free_shipping": "Masz darmową dostawę! 🎉"
},
    "hu": {
    "currency": "Ft",
    "free_shipping": "18990",
    "free_shipping_from": "Ingyenes szállítás 18990 Ft felett",
    "missing_free_shipping": "Az ingyenes kiszállításhoz hiányzik",
    "watchdog_title": "Szeretnél várni?",
    "you_have_free_shipping": "A kiszállítás számodra ingyenes! 🎉"
},
    "de": {
    "currency": "EUR",
    "free_shipping": "49",
    "free_shipping_from": "Kostenloser Versand ab 49 EUR",
    "missing_free_shipping": "Für den kostenlosen Versand fehlen",
    "watchdog_title": "Möchtest du warten?",
    "you_have_free_shipping": "Kostenloser Versand! 🎉"
},
    "at": {
    "currency": "EUR",
    "free_shipping": "49",
    "free_shipping_from": "Kostenloser Versand ab 49 EUR",
    "missing_free_shipping": "Für den kostenlosen Versand fehlen",
    "watchdog_title": "Möchtest du warten?",
    "you_have_free_shipping": "Kostenloser Versand! 🎉"
},
    "ro": {
    "currency": "Lei",
    "free_shipping": "199",
    "free_shipping_from": "Transport gratuit de la 199 Lei",
    "missing_free_shipping": "Până la transport gratuit lipsesc",
    "watchdog_title": "Dorești să aștepți?",
    "you_have_free_shipping": "Aveți transport gratuit! 🎉"
},
    "fr": {
    "currency": "€",
    "free_shipping": "49",
    "free_shipping_from": "Livraison gratuite à partir de 49 €",
    "missing_free_shipping": "Pour bénéficier de la livraison gratuite, il vous manque",
    "watchdog_title": "Voulez-vous patienter ?",
    "you_have_free_shipping": "Vous bénéficiez de la livraison gratuite ! 🎉"
},
    "it": {
    "currency": "€",
    "free_shipping": "49",
    "free_shipping_from": "Spedizione gratuita a partire da 49 €",
    "missing_free_shipping": "Alla spedizione gratuita manca",
    "watchdog_title": "Vuoi aspettare?",
    "you_have_free_shipping": "Hai la spedizione gratuita! 🎉"
}
  }
</i18n>
